import React, { FC, ClipboardEvent } from "react";

import ReactDropzone, {
  FileRejection,
  FileError,
  ErrorCode,
} from "react-dropzone";

import { convertByte } from "~/utils";

import { FILE_MAX_BYTE_SIZE, ACCEPT_IMAGE_FILE_TYPES } from "~/constants/file";

import { ButtonWithIcon, Icon, Label } from "~/ui";

type PropsType = {
  noClick?: boolean;
  fileClassName?: string;
  multiple?: boolean;
  onDropFiles: (acceptedFiles: File[], fileRejections: FileRejection[]) => void;
  onRemoveFile?: () => void;
  acceptFileTypes?: Record<string, string[]>;
  label?: string;
  required?: boolean;
  maxFileByte?: number;
  minFileByte?: number;
  readonly?: boolean;
  maxFiles?: number;
  fileName?: string;
  noFilesText: string;
};

export const DropzoneFileFieldWithFile: FC<PropsType> = ({
  noClick = false,
  multiple = false,
  onDropFiles,
  onRemoveFile,
  acceptFileTypes = ACCEPT_IMAGE_FILE_TYPES,
  label,
  required = false,
  maxFileByte = FILE_MAX_BYTE_SIZE,
  minFileByte = 0,
  fileClassName,
  readonly = false,
  fileName,
  maxFiles,
  noFilesText,
}: PropsType) => {
  const jaErrorMessage = (error: FileError) => {
    switch (error.code as ErrorCode) {
      case ErrorCode.FileInvalidType:
        return `アップロードできるファイルの拡張子は ${Object.values(
          acceptFileTypes,
        ).join(",")} です`;
      case ErrorCode.FileTooLarge:
        return `アップロードできる最大ファイルサイズは${convertByte(
          maxFileByte,
        )}です`;
      case ErrorCode.FileTooSmall:
        return `アップロードできる最小ファイルサイズは${convertByte(
          minFileByte,
        )}です`;
      case ErrorCode.TooManyFiles:
        return `アップロードできるファイルは${maxFiles || 1}つです`;
      default:
        return error.message;
    }
  };

  const jaFileRejections = (fileRejections: FileRejection[]) => {
    return fileRejections.map((reject) => ({
      file: reject.file,
      errors: reject.errors.map((error) => ({
        code: error.code,
        message: jaErrorMessage(error),
      })),
    }));
  };

  // ファイルの拡張子を取得するヘルパー関数
  const getFileExtension = (file: File): string =>
    `.${file.name.split(".").pop()?.toLowerCase() || ""}`;

  // ペーストされたファイルを処理する関数
  const handlePastedFile = (
    file: File,
    acceptedFiles: File[],
    fileRejections: FileRejection[],
  ): void => {
    const acceptedFileValues = Object.values(acceptFileTypes).flat();

    if (acceptedFileValues.includes(getFileExtension(file))) {
      acceptedFiles.push(file);
    } else {
      fileRejections.push({
        file,
        errors: [
          {
            code: ErrorCode.FileInvalidType,
            message: `アップロードできるファイルの拡張子は ${acceptedFileValues.join(
              ",",
            )} です`,
          },
        ],
      });
    }
  };

  const handlePaste = (
    e: ClipboardEvent,
    callback: (acceptedFiles: File[], fileRejections: FileRejection[]) => void,
  ) => {
    const acceptedFiles: File[] = [];
    const fileRejections: FileRejection[] = [];

    Array.from(e.clipboardData.items)
      .filter((item) => item.kind === "file" && item.getAsFile())
      .forEach((item) =>
        handlePastedFile(
          item.getAsFile() as File,
          acceptedFiles,
          fileRejections,
        ),
      );

    callback(acceptedFiles, fileRejections);
  };

  const handleRemoveFile = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault();
    e.stopPropagation();

    const isConfirm = confirm("本当に削除しますか？");
    if (!isConfirm) return;

    onRemoveFile && onRemoveFile();
  };

  return (
    <div className="w-full">
      {label && (
        <Label
          htmlFor=""
          labelText={label}
          required={required}
          className="block mb-2"
        />
      )}
      <ReactDropzone
        maxFiles={maxFiles}
        noClick={noClick}
        disabled={readonly}
        onDrop={(acceptedFiles, fileRejections) =>
          onDropFiles(acceptedFiles, jaFileRejections(fileRejections))
        }
        accept={acceptFileTypes}
        multiple={multiple}
        maxSize={maxFileByte}
      >
        {({ getRootProps, getInputProps, isDragActive }) => (
          <div
            className={`
              relative w-full min-h-[120px]
              border-2 border-dashed rounded-lg
              transition-all duration-200 ease-in-out
              ${
                isDragActive
                  ? "border-primary-600 bg-primary-200 shadow-xs"
                  : fileName
                    ? "border-primary-400 bg-primary-50"
                    : "border-gray-300 bg-gray-50 hover:border-gray-400 hover:bg-gray-100"
              }
              ${readonly ? "opacity-60 cursor-not-allowed" : "cursor-pointer"}
              ${fileClassName}
            `}
            {...getRootProps({
              onPaste: (e) =>
                handlePaste(e, (acceptedFiles, fileRejections) =>
                  onDropFiles(acceptedFiles, jaFileRejections(fileRejections)),
                ),
            })}
          >
            <input {...getInputProps()} />

            <div className="flex items-center justify-center p-6 h-full">
              {fileName ? (
                <div className="flex items-center justify-between w-full">
                  <div className="flex items-center space-x-3">
                    <div className="flex items-center justify-center w-12 h-12 bg-primary-100 rounded-full">
                      <Icon
                        icon="ioDocumentTextOutline"
                        size="1.5rem"
                        color="text-primary-600"
                      />
                    </div>
                    <div className="flex flex-col">
                      <p className="text-sm font-medium text-gray-900 truncate max-w-[200px]">
                        {fileName}
                      </p>
                      <p className="text-xs text-gray-500">
                        ファイルが選択されています
                      </p>
                    </div>
                  </div>
                  {onRemoveFile && !readonly && (
                    <ButtonWithIcon
                      icon={{
                        icon: "ioCloseOutline",
                        size: "1.25rem",
                        color: "text-gray-400",
                      }}
                      onClick={handleRemoveFile}
                      disabled={readonly}
                      className="ml-4 p-2 hover:bg-red-50 rounded-full transition-colors"
                      withHoverEffect={true}
                    />
                  )}
                </div>
              ) : (
                <div className="text-center">
                  <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full">
                    <Icon
                      icon={
                        isDragActive
                          ? "ioCloudDownloadOutline"
                          : "ioImageOutline"
                      }
                      size="2rem"
                      color={
                        isDragActive ? "text-primary-600" : "text-gray-400"
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <p className="text-sm font-medium text-gray-900">
                      {isDragActive
                        ? "ファイルをドロップしてください"
                        : noFilesText}
                    </p>
                    <p className="text-xs text-gray-500">
                      {isDragActive
                        ? ""
                        : "クリックしてファイルを選択するか、ドラッグ＆ドロップしてください"}
                    </p>
                    {!readonly && (
                      <div className="flex items-center justify-center space-x-4 text-xs text-gray-400">
                        <span>
                          対応形式:{" "}
                          {Object.values(acceptFileTypes).flat().join(", ")}
                        </span>
                        <span>•</span>
                        <span>最大: {convertByte(maxFileByte)}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {isDragActive && (
              <div className="absolute inset-0 bg-primary-100/50 rounded-lg flex items-center justify-center">
                <div className="text-primary-600 font-medium">
                  ファイルをドロップしてアップロード
                </div>
              </div>
            )}
          </div>
        )}
      </ReactDropzone>
    </div>
  );
};
